import './PaymentDisplayArea.pcss';
import React, { useMemo } from 'react';
import { isNumber } from 'lodash';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import { centsToDollars } from '@mm/utils';
import { Line } from '@mm/components';
import classNames from 'classnames';

type Props = {
  plan: EnhancedPlan;
  isUpgradeOrDowngrade: boolean;
  pricesObject: {
    newSendCreditsValue: number | null;
    currentSendCreditsValue: number;
    currentMonthlyFee: number;
    currentUserSeatsValue: number | null;
    currentUserSeatsPrice: number | null;
    newUserSeatsPrice: number | null;
    newUserSeatsValue: number | null;
    currentSendCreditsPrice: number | null;
    newSendCreditsPrice: number;
    newMonthlyFee: number;
    payNow: number;
    proRatedAmount: number | null;
    proRatedAsCredit: number | null | undefined;
    newSmsNumberCount: number | null;
    smsNumberCount: number;
    currentSmsNumberPrice: number | null;
    newSmsNumberPrice: number;
    showAddCoupon: boolean;
    planPrice: number | null;
  };
  isAnnual: boolean;
  hideUnchangedFields?: boolean;
};
// tslint:disable-next-line:cyclomatic-complexity
export const PaymentDisplayArea = (props: Props) => {
  const {
    plan,
    pricesObject,
    isUpgradeOrDowngrade,
    isAnnual,
    hideUnchangedFields,
  } = props;

  const printValue = (value: number) => {
    return value === -1 ? '∞' : value;
  };

  const {
    planPrice,
    newSendCreditsValue,
    currentSendCreditsValue,
    currentMonthlyFee,
    currentUserSeatsValue,
    currentUserSeatsPrice,
    newUserSeatsPrice,
    newUserSeatsValue,
    currentSendCreditsPrice,
    newSendCreditsPrice,
    newMonthlyFee,
    payNow,
    proRatedAmount,
    proRatedAsCredit,
    newSmsNumberCount,
    smsNumberCount,
    currentSmsNumberPrice,
    newSmsNumberPrice,
  } = pricesObject;

  const basePrice = useMemo(
    () =>
      isUpgradeOrDowngrade || !planPrice ? plan.unitAmountInCents : planPrice,
    [isUpgradeOrDowngrade, plan.unitAmountInCents, planPrice],
  );

  // Note: initial implementation only covers new subscription case.
  // More logic needs to be added when existing plan updates are added.
  const yearlyPrice = useMemo(() => {
    // Note: some plans do not have discounted annualUnitAmountInCents price defined
    const monthlyPrice = basePrice / 12;
    const dollarMonthlyPrice = centsToDollars(monthlyPrice, !isAnnual);

    const dollarYearlyPrice = isAnnual && centsToDollars(basePrice, !isAnnual);

    const formattedPrice = isAnnual
      ? `${dollarYearlyPrice} ($${dollarMonthlyPrice}/month)`
      : dollarMonthlyPrice;

    return formattedPrice;
  }, [basePrice, isAnnual]);

  const price = isAnnual ? yearlyPrice : centsToDollars(basePrice);
  const planNameClassName = isAnnual
    ? 'payment-display-area__plan-name-annual'
    : '';

  return (
    <div>
      <div className="payment-display-area__columns">
        <div className="payment-display-area__column-title">Old price</div>
        <div className="payment-display-area__column-title payment-display-area__column-newprice">
          New price
        </div>
      </div>
      <div className="payment-display-area">
        <div className="payment-display-area__line">
          <div
            className={classNames(
              planNameClassName,
              'payment-display-area__line-left',
            )}
          >
            {plan.shortName}
          </div>
          <div
            className={classNames(
              planNameClassName,
              'payment-display-area__line-right',
              'payment-display-area__prev-new-price',
            )}
          >
            ${price}
          </div>
        </div>
        <Line className="payment-display-area__c-line" />
        <div className="payment-display-area__line">
          <div className="payment-display-area__line-left">
            Credit blocks
            <span className="payment-display-area__prev-price">
              {printValue(currentSendCreditsValue).toLocaleString('en-US')}
            </span>
            {isNumber(newSendCreditsValue) && (
              <span className="payment-display-area__line-arrow">&#8594;</span>
            )}
            {isNumber(newSendCreditsValue) && (
              <span className="payment-display-area__prev-new-price">
                {printValue(newSendCreditsValue).toLocaleString('en-US')}
              </span>
            )}
          </div>
          <div className="payment-display-area__line-right">
            {isNumber(currentSendCreditsPrice) && (
              <span className="payment-display-area__prev-price">
                ${currentSendCreditsPrice.toFixed(2).replace(/[.,]00$/, '')}
              </span>
            )}
            {isNumber(currentSendCreditsPrice) && (
              <span className="payment-display-area__line-arrow">&#8594;</span>
            )}
            <span className="payment-display-area__prev-new-price">
              {' '}
              ${newSendCreditsPrice.toFixed(2).replace(/[.,]00$/, '')}
            </span>
          </div>
        </div>

        {Boolean(hideUnchangedFields && newUserSeatsValue) ||
          (!hideUnchangedFields && (
            <>
              <Line className="payment-display-area__c-line" />
              <div className="payment-display-area__line">
                <div className="payment-display-area__line-left">
                  User seats
                  {isNumber(currentUserSeatsValue) && (
                    <span className="payment-display-area__prev-price">
                      {printValue(currentUserSeatsValue)}
                    </span>
                  )}
                  {isNumber(newUserSeatsValue) && (
                    <span className="payment-display-area__line-arrow">
                      &#8594;
                    </span>
                  )}
                  {isNumber(newUserSeatsValue) && (
                    <span className="payment-display-area__prev-new-price">
                      {printValue(newUserSeatsValue)}
                    </span>
                  )}
                </div>
                <div className="payment-display-area__line-right">
                  {isNumber(currentUserSeatsPrice) && (
                    <span className="payment-display-area__prev-price">
                      ${currentUserSeatsPrice.toFixed(2).replace(/[.,]00$/, '')}
                    </span>
                  )}
                  {isNumber(currentUserSeatsPrice) && (
                    <span className="payment-display-area__line-arrow">
                      &#8594;
                    </span>
                  )}
                  <span className="payment-display-area__prev-new-price">
                    {' '}
                    ${newUserSeatsPrice?.toFixed(2).replace(/[.,]00$/, '')}
                  </span>
                </div>
              </div>
            </>
          ))}

        {Boolean(hideUnchangedFields && newSmsNumberCount) ||
          (!hideUnchangedFields && (
            <>
              <Line className="payment-display-area__c-line" />
              <div className="payment-display-area__line">
                <div className="payment-display-area__line-left">
                  SMS Numbers
                  <span className="payment-display-area__prev-price">
                    {printValue(smsNumberCount)}
                  </span>
                  {isNumber(newSmsNumberCount) && (
                    <span className="payment-display-area__line-arrow">
                      &#8594;
                    </span>
                  )}
                  {isNumber(newSmsNumberCount) && (
                    <span className="payment-display-area__prev-new-price">
                      {printValue(newSmsNumberCount)}
                    </span>
                  )}
                </div>
                <div className="payment-display-area__line-right">
                  {isNumber(currentSmsNumberPrice) && (
                    <span className="payment-display-area__prev-price">
                      ${currentSmsNumberPrice.toFixed(2).replace(/[.,]00$/, '')}
                    </span>
                  )}
                  {isNumber(currentSmsNumberPrice) && (
                    <span className="payment-display-area__line-arrow">
                      &#8594;
                    </span>
                  )}
                  <span className="payment-display-area__prev-new-price">
                    {' '}
                    ${newSmsNumberPrice.toFixed(2).replace(/[.,]00$/, '')}
                  </span>
                </div>
              </div>
            </>
          ))}
        <Line className="payment-display-area__c-line" />
        {Boolean(proRatedAmount && !proRatedAsCredit) && (
          <>
            <div className="payment-display-area__line payment-display-area--prorated">
              <div className="payment-display-area__line-left">
                Pro-rated payment due today
              </div>
              <div className="payment-display-area__line-right payment-display-area__prev-new-price">
                ${proRatedAmount?.toFixed(2).replace(/[.,]00$/, '')}
              </div>
            </div>
            <Line className="payment-display-area__c-line" />
          </>
        )}
        {Boolean(proRatedAmount && proRatedAsCredit) && (
          <div>
            <div className="payment-display-area__line payment-display-area--prorated">
              <div className="payment-display-area__line-left">
                Pro-rated payment due today
              </div>
              <div className="payment-display-area__line-right payment-display-area__prev-new-price">
                $0
              </div>
            </div>
            <Line className="payment-display-area__c-line" />

            <div className="payment-display-area__line payment-display-area--pay-now-credit">
              <div className="payment-display-area__line-left">
                Credit for future payments
              </div>
              <div className="payment-display-area__line-right payment-display-area__prev-new-price">
                ${centsToDollars(proRatedAsCredit || 0).replace(/[.,]00$/, '')}
              </div>
            </div>
            <Line className="payment-display-area__c-line" />
          </div>
        )}
      </div>
      {!isAnnual && (
        <>
          <div className="payment-display-area__montly-fee-wrapper">
            <div className="payment-display-area__line-left">Monthly fee</div>
            <div className="payment-display-area__line-right">
              <span className="payment-display-area__prev-price">
                ${currentMonthlyFee.toFixed(2).replace(/[.,]00$/, '')}
              </span>
              <span className="payment-display-area__line-arrow">&#8594;</span>
              <span> ${newMonthlyFee.toFixed(2).replace(/[.,]00$/, '')}</span>
            </div>
          </div>
          <Line className="payment-display-area__c-line" />
        </>
      )}
      <div className="payment-display-area--pay-now payment-display-area__total">
        <div className="payment-display-area__total-left">TOTAL: </div>
        <div className="payment-display-area__total-right">
          <span>
            {' '}
            $
            {payNow &&
              payNow
                .toFixed(2)
                .replace(/[.,]00$/, '')
                .toUpperCase()}
          </span>
        </div>
      </div>
    </div>
  );
};
